import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User,
  Building2,
  Users as UsersIcon,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Plus,
  Trash2,
  ShieldCheck,
  Globe,
  Briefcase,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const STEPS = [
  { key: "profile", label: "Your profile", icon: User },
  { key: "org", label: "Organization", icon: Building2 },
  { key: "team", label: "Invite team", icon: UsersIcon },
  { key: "sample", label: "Workspace", icon: Sparkles },
];

const INDUSTRY_OPTIONS = [
  "Software & SaaS",
  "Legal & Professional Services",
  "Financial Services",
  "Healthcare",
  "Real Estate",
  "Retail & E-commerce",
  "Manufacturing",
  "Education",
  "Non-profit",
  "Other",
];
const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.key} className="flex items-center gap-2">
            <div
              className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                done
                  ? "bg-emerald-600 text-white"
                  : active
                    ? "bg-slate-900 text-white"
                    : "bg-slate-200 text-slate-500"
              }`}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={`hidden sm:inline text-xs font-medium ${
                active ? "text-slate-900" : "text-slate-500"
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div className="w-6 sm:w-10 h-px bg-slate-200 mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1: Profile
  const [profile, setProfile] = useState({
    name: user?.name || "",
    title: user?.title || "",
    phone: user?.phone || "",
  });

  // Step 2: Organization
  const [org, setOrg] = useState({
    name: "",
    email_domain: (user?.email || "").split("@")[1] || "",
    phone: "",
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    industry: "Software & SaaS",
    size: "1-10",
    address: {
      line1: "",
      city: "",
      region: "",
      postal_code: "",
      country: "",
    },
  });

  // Prefill org name from existing org doc (set by /auth/register)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/org");
        if (cancelled) return;
        setOrg((prev) => ({
          ...prev,
          name: prev.name || data.name || "",
          email_domain: prev.email_domain || data.email_domain || prev.email_domain,
          timezone: data.timezone || prev.timezone,
        }));
      } catch {
        /* fine — onboarding will still work */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Step 3: Invites
  const [invites, setInvites] = useState([]);
  const [newInvite, setNewInvite] = useState({
    email: "",
    name: "",
    role: "sender",
  });

  // Step 4: Sample data
  const [seedSample, setSeedSample] = useState(true);

  const next = async () => {
    if (step === 0) {
      if (!profile.name.trim()) return toast.error("Your name is required");
    }
    if (step === 1) {
      if (!org.name.trim()) return toast.error("Organization name is required");
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };
  const prev = () => setStep(Math.max(0, step - 1));

  const addInvite = () => {
    if (!newInvite.email || !newInvite.name) {
      return toast.error("Name and email are required");
    }
    if (invites.find((i) => i.email === newInvite.email.toLowerCase())) {
      return toast.error("Already added");
    }
    setInvites([
      ...invites,
      {
        ...newInvite,
        email: newInvite.email.toLowerCase(),
      },
    ]);
    setNewInvite({ email: "", name: "", role: "sender" });
  };

  const finish = async () => {
    setBusy(true);
    try {
      // 1. Load org to get current name, then merge
      // We send the updated org details + profile + sample flag in one call
      await api.post("/org/onboarding/complete", {
        profile: {
          name: profile.name,
          title: profile.title || undefined,
          phone: profile.phone || undefined,
        },
        org: {
          name: org.name,
          email_domain: org.email_domain || undefined,
          phone: org.phone || undefined,
          timezone: org.timezone,
          industry: org.industry,
          size: org.size,
          address: org.address,
        },
        create_sample_data: seedSample,
      });
      // 2. Send invitations
      for (const inv of invites) {
        try {
          await api.post("/invitations", inv);
        } catch (e) {
          // Don't block onboarding for individual invite failures
          console.warn("invite failed", inv.email, e);
        }
      }
      await refresh();
      toast.success("Workspace ready. Welcome to Attestra.");
      navigate("/app", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="onboarding-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/app" className="flex items-center">
            <Logo className="h-7" />
          </Link>
          <StepIndicator current={step} />
          <div className="text-xs text-slate-500 hidden md:block">
            Step {step + 1} of {STEPS.length}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Step 1 — Profile */}
        {step === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="onboarding-step-profile">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              <User className="h-3.5 w-3.5" /> Step 1 of 4
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Welcome to Attestra. Let's get you set up.
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              First, a couple of details about you. This will appear on documents and
              audit trails you send.
            </p>

            <div className="mt-8 grid md:grid-cols-2 gap-5">
              <div>
                <Label>Your full name</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="mt-1.5 h-11"
                  data-testid="onb-name"
                />
              </div>
              <div>
                <Label>Job title</Label>
                <Input
                  value={profile.title}
                  onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                  placeholder="VP, People Operations"
                  className="mt-1.5 h-11"
                  data-testid="onb-title"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Phone (optional)</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+1 555 0100"
                  className="mt-1.5 h-11"
                  data-testid="onb-phone"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Org */}
        {step === 1 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="onboarding-step-org">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              <Building2 className="h-3.5 w-3.5" /> Step 2 of 4
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Tell us about your organization.
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              These details appear on agreements, invitation emails, and completion
              certificates.
            </p>

            <div className="mt-8 grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label>Organization name</Label>
                <Input
                  value={org.name}
                  onChange={(e) => setOrg({ ...org, name: e.target.value })}
                  placeholder="Acme Inc."
                  className="mt-1.5 h-11"
                  data-testid="onb-org-name"
                />
              </div>
              <div>
                <Label>
                  <Globe className="h-3 w-3 inline mr-1" />
                  Email domain
                </Label>
                <Input
                  value={org.email_domain}
                  onChange={(e) => setOrg({ ...org, email_domain: e.target.value })}
                  placeholder="acme.com"
                  className="mt-1.5 h-11"
                  data-testid="onb-org-domain"
                />
              </div>
              <div>
                <Label>Business phone</Label>
                <Input
                  value={org.phone}
                  onChange={(e) => setOrg({ ...org, phone: e.target.value })}
                  placeholder="+1 555 0199"
                  className="mt-1.5 h-11"
                  data-testid="onb-org-phone"
                />
              </div>
              <div>
                <Label>
                  <Briefcase className="h-3 w-3 inline mr-1" />
                  Industry
                </Label>
                <Select
                  value={org.industry}
                  onValueChange={(v) => setOrg({ ...org, industry: v })}
                >
                  <SelectTrigger className="mt-1.5 h-11" data-testid="onb-org-industry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_OPTIONS.map((i) => (
                      <SelectItem key={i} value={i}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Company size</Label>
                <Select
                  value={org.size}
                  onValueChange={(v) => setOrg({ ...org, size: v })}
                >
                  <SelectTrigger className="mt-1.5 h-11" data-testid="onb-org-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s} people
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Timezone</Label>
                <Select
                  value={org.timezone}
                  onValueChange={(v) => setOrg({ ...org, timezone: v })}
                >
                  <SelectTrigger className="mt-1.5 h-11" data-testid="onb-org-tz">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 pt-3 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                  Address (optional)
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Street</Label>
                <Input
                  value={org.address.line1}
                  onChange={(e) =>
                    setOrg({ ...org, address: { ...org.address, line1: e.target.value } })
                  }
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={org.address.city}
                  onChange={(e) =>
                    setOrg({ ...org, address: { ...org.address, city: e.target.value } })
                  }
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label>Region / State</Label>
                <Input
                  value={org.address.region}
                  onChange={(e) =>
                    setOrg({ ...org, address: { ...org.address, region: e.target.value } })
                  }
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label>Postal code</Label>
                <Input
                  value={org.address.postal_code}
                  onChange={(e) =>
                    setOrg({ ...org, address: { ...org.address, postal_code: e.target.value } })
                  }
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={org.address.country}
                  onChange={(e) =>
                    setOrg({ ...org, address: { ...org.address, country: e.target.value } })
                  }
                  className="mt-1.5 h-11"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Team */}
        {step === 2 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="onboarding-step-team">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              <UsersIcon className="h-3.5 w-3.5" /> Step 3 of 4
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Invite your team (optional).
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Your teammates will receive a secure invitation link — no password needed to
              accept. You can always invite more people later from Team settings.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end">
              <div>
                <Label>Name</Label>
                <Input
                  value={newInvite.name}
                  onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                  className="mt-1.5 h-11"
                  placeholder="Rahul Mehta"
                  data-testid="invite-new-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  className="mt-1.5 h-11"
                  placeholder="rahul@acme.com"
                  data-testid="invite-new-email"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={newInvite.role}
                  onValueChange={(v) => setNewInvite({ ...newInvite, role: v })}
                >
                  <SelectTrigger className="mt-1.5 h-11" data-testid="invite-new-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org_admin">Org admin</SelectItem>
                    <SelectItem value="hr_manager">HR manager</SelectItem>
                    <SelectItem value="sales_ops">Sales / Ops</SelectItem>
                    <SelectItem value="sender">Sender</SelectItem>
                    <SelectItem value="approver">Approver</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={addInvite}
                className="h-11 bg-slate-900 text-white hover:bg-slate-800"
                data-testid="invite-add-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            {invites.length > 0 && (
              <div className="mt-6 rounded-lg border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_140px_60px] px-4 py-2.5 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  <div>Name</div>
                  <div>Email</div>
                  <div>Role</div>
                  <div></div>
                </div>
                {invites.map((inv) => (
                  <div
                    key={inv.email}
                    className="grid grid-cols-[1fr_1fr_140px_60px] items-center px-4 py-3 border-t border-slate-100"
                    data-testid={`invite-row-${inv.email}`}
                  >
                    <div className="text-sm">{inv.name}</div>
                    <div className="text-sm text-slate-600">{inv.email}</div>
                    <div className="text-xs capitalize">
                      {inv.role.replace(/_/g, " ")}
                    </div>
                    <button
                      onClick={() =>
                        setInvites(invites.filter((i) => i.email !== inv.email))
                      }
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500 justify-self-end"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4 — Sample data */}
        {step === 3 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="onboarding-step-sample">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              <Sparkles className="h-3.5 w-3.5" /> Step 4 of 4
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Get a head start with sample workspace.
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              We can pre-load a few demo documents and templates so you can explore features
              end-to-end before you send your first real agreement.
            </p>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <button
                onClick={() => setSeedSample(true)}
                className={`text-left rounded-xl border p-5 transition-all ${
                  seedSample
                    ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/40"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid="sample-yes-btn"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-600" />
                  <div className="font-semibold">Yes, add samples</div>
                </div>
                <div className="mt-3 text-sm text-slate-600 space-y-1.5">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    Sample NDA (completed with audit trail)
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    Sample offer letter draft
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    Reusable NDA template
                  </div>
                </div>
              </button>
              <button
                onClick={() => setSeedSample(false)}
                className={`text-left rounded-xl border p-5 transition-all ${
                  !seedSample
                    ? "border-slate-900 ring-2 ring-slate-900/20"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid="sample-no-btn"
              >
                <div className="font-semibold">Start empty</div>
                <div className="mt-3 text-sm text-slate-600">
                  Clean workspace. Upload your first real document now and send it.
                </div>
              </button>
            </div>

            <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div className="text-xs text-slate-600 leading-relaxed">
                You're the org admin for <span className="font-semibold">{org.name || "your workspace"}</span>.
                You can change any of these details later from Settings.
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prev}
            disabled={step === 0 || busy}
            data-testid="onb-prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={next}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="onb-next-btn"
            >
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={finish}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="onb-finish-btn"
            >
              {busy ? "Finishing…" : "Finish setup"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
